'use client'

import { useState } from 'react'
import { INDUSTRY_OPTIONS, COMPANY_SIZE_OPTIONS } from '@/lib/validations/company.schema'
import { updateCompanyProfile } from '../_actions/company-actions'
import { BannerUpload } from '../manage/_components/banner-upload'
import type { CompanyProfileData } from '@/types/api.types'

interface EditCompanyModalProps {
  company: CompanyProfileData
  onClose: () => void
}

export function EditCompanyModal({ company, onClose }: EditCompanyModalProps) {
  const [name, setName] = useState(company.name)
  const [industry, setIndustry] = useState(company.industry)
  const [industryOther, setIndustryOther] = useState(company.industryOther ?? '')
  const [size, setSize] = useState(company.size)
  const [website, setWebsite] = useState(company.website ?? '')
  const [description, setDescription] = useState(company.description ?? '')
  const [adminName, setAdminName] = useState(company.adminName)
  const [adminRole, setAdminRole] = useState(company.adminRole)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  /** Returns a validation error string or '' if the field is valid. */
  function validate(): string {
    const errs: Record<string, string> = {}
    if (!name.trim()) errs.name = 'Company name is required.'
    if (website && !/^https?:\/\/.+/.test(website.trim())) {
      errs.website = 'Website must be a valid URL starting with https://'
    }
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return 'Please fix the errors below.'
    return ''
  }

  const isRejected = company.status === 'rejected'
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }

  const hasChanges =
    name !== company.name ||
    industry !== company.industry ||
    industryOther !== (company.industryOther ?? '') ||
    size !== company.size ||
    website !== (company.website ?? '') ||
    description !== (company.description ?? '') ||
    adminName !== company.adminName ||
    adminRole !== company.adminRole
  const inputClass = 'h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-text-muted)] bg-[var(--color-bg-surface)] px-3 text-sm text-[var(--color-text-primary)] outline-none transition-colors placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-brand-indigo)] focus:ring-2 focus:ring-[var(--color-brand-indigo)]/25'
  const labelClass = 'text-sm font-medium text-[var(--color-text-secondary)]'

  async function handleSave() {
    setError('')
    setFieldErrors({})

    // Client-side validation first
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setIsSubmitting(true)

    try {
      const result = await updateCompanyProfile(company.id, {
        name: name.trim(),
        industry: industry as (typeof INDUSTRY_OPTIONS)[number],
        industryOther: industry === 'Other' ? industryOther : undefined,
        size: size as (typeof COMPANY_SIZE_OPTIONS)[number],
        website: website.trim() || undefined,
        description: description || undefined,
        adminName,
        adminRole,
      })

      if (!result.success) {
        // Surface field-level errors returned by the server
        if ('fieldErrors' in result && result.fieldErrors) {
          const flat: Record<string, string> = {}
          for (const [key, msgs] of Object.entries(result.fieldErrors)) {
            if (msgs && msgs.length > 0) flat[key] = msgs[0]
          }
          setFieldErrors(flat)
        }
        setError(result.error || 'Update failed. Please check the fields above.')
        return
      }

      // Reload page to reflect changes
      window.location.reload()
    } catch {
      setError('Update failed. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-label="Edit company profile">
      <div className="w-full max-w-lg rounded-xl bg-[var(--color-bg-base)] p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-[var(--color-text-primary)]" style={font}>
            Edit company profile
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M6 6l8 8M14 6l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-error)]/20 bg-[var(--color-error)]/10 p-3" role="alert">
            <p className="text-sm text-[var(--color-error)]" style={font}>{error}</p>
          </div>
        )}

        {/* Banner upload */}
        <div className="mb-4 flex flex-col gap-1.5">
          <p className="text-sm font-medium text-[var(--color-text-secondary)]" style={font}>
            Company banner
          </p>
          <BannerUpload
            currentBannerUrl={company.bannerUrl}
            companyId={company.id}
            onUploaded={() => window.location.reload()}
          />
        </div>

        <div className="flex flex-col gap-4">
          {/* Company name */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass} style={font}>
              Company name <span className="text-[var(--color-error)]">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`${inputClass}${fieldErrors.name ? ' border-[var(--color-error)]' : ''}`}
              style={font}
              aria-invalid={!!fieldErrors.name}
            />
            {fieldErrors.name && (
              <p className="text-xs text-[var(--color-error)]" style={font}>{fieldErrors.name}</p>
            )}
          </div>

          {/* Industry */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass} style={font}>Industry</label>
            <select value={industry} onChange={(e) => setIndustry(e.target.value)} className={inputClass} style={font}>
              {INDUSTRY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {industry === 'Other' && (
            <div className="flex flex-col gap-1.5">
              <input value={industryOther} onChange={(e) => setIndustryOther(e.target.value)} placeholder="Specify your industry" className={inputClass} style={font} />
            </div>
          )}

          {/* Size */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass} style={font}>Company size</label>
            <div className="flex flex-wrap gap-2">
              {COMPANY_SIZE_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => setSize(opt)}
                  className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                    size === opt
                      ? 'border-[var(--color-brand-indigo)] bg-[var(--color-brand-indigo)]/10 text-[var(--color-brand-indigo)] font-medium'
                      : 'border-[var(--color-text-muted)] text-[var(--color-text-secondary)]'
                  }`}
                  style={font}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* Website */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass} style={font}>Website</label>
            <input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://acme.com"
              className={`${inputClass}${fieldErrors.website ? ' border-[var(--color-error)]' : ''}`}
              style={font}
              aria-invalid={!!fieldErrors.website}
            />
            {fieldErrors.website && (
              <p className="text-xs text-[var(--color-error)]" style={font}>{fieldErrors.website}</p>
            )}
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass} style={font}>About</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full rounded-[var(--radius-md)] border border-[var(--color-text-muted)] bg-[var(--color-bg-surface)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none resize-none focus:border-[var(--color-brand-indigo)] focus:ring-2 focus:ring-[var(--color-brand-indigo)]/25"
              style={font}
            />
            <span className="text-xs text-[var(--color-text-muted)] text-right" style={font}>
              {description.length}/500
            </span>
          </div>

          {/* Admin name */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass} style={font}>Contact name</label>
            <input value={adminName} onChange={(e) => setAdminName(e.target.value)} className={inputClass} style={font} />
          </div>

          {/* Admin role */}
          <div className="flex flex-col gap-1.5">
            <label className={labelClass} style={font}>Contact role</label>
            <input value={adminRole} onChange={(e) => setAdminRole(e.target.value)} className={inputClass} style={font} />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 rounded-[var(--radius-md)] border border-[var(--color-text-muted)] px-4 py-2.5 text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated)] disabled:opacity-50"
            style={font}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSubmitting || !hasChanges}
            className="flex-[2] rounded-[var(--radius-md)] bg-[var(--color-brand-indigo)] px-4 py-2.5 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50"
            style={font}
          >
            {isSubmitting ? 'Saving...' : isRejected ? 'Save & resubmit' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
